import { ScrapbookContentType } from '@/types';
import { Link, ResponsiveImage, UserContext } from '@/components';
import classNames from 'classnames';
import dayjs from 'dayjs';
import ImageIcon from '@/assets/imageIcon.svg';
import { useContext, useEffect, useRef, useState } from 'react';
import ScissorIcon from '@/assets/scissors-light.svg';
import ClockIcon from '@/assets/clockLight.svg';
import EyeIcon from '@/assets/eye-light.svg';
import CheckboxCircleIcon from '@/assets/checkboxCircle.svg';
import ReportButton from '../common/ReportButton';
import { useOptimizedPreview } from '../scrapbook/previews/OptimizedPreview';
import { useTranslations } from 'next-intl';
import { SaveToScrapbookButton } from '../scrapbook';
import { SaveToScrapbookButtonMethods } from '../scrapbook/SaveToScrapbookButton';
import { ReportListCardModel } from './ReportListCard';

const ReportListCardStylus = ({
  data,
  className,
  small,
  onClick,
  hideViewed = false,
  showActionButtons,
  ...props
}: ReportListCardModel) => {
  const user = useContext(UserContext);
  const { openReportPreview } = useOptimizedPreview();
  const [isVisitedPage, setIsVisitedPage] = useState(false);
  const t = useTranslations();

  const saveToScrapbookButton = useRef<SaveToScrapbookButtonMethods>(null);

  useEffect(() => {
    const checkVisitedStatus = () => {
      const visitedIds = localStorage.getItem('visitedPageIds');
      const reportIdStr = data.reportId?.toString();
      const visitedArray = visitedIds ? visitedIds.split(',') : [];
      const isVisited = visitedArray.includes(reportIdStr);
      setIsVisitedPage(isVisited);
    };

    checkVisitedStatus();

    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'visitedPageIds') {
        checkVisitedStatus();
      }
    };

    const handleVisitedUpdate = () => {
      checkVisitedStatus();
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('visitedPagesUpdated', handleVisitedUpdate);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('visitedPagesUpdated', handleVisitedUpdate);
    };
  }, [data.reportId, hideViewed]);

  const handlePreview = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (data.reportId) {
      openReportPreview(data.reportId.toString());
    }
  };

  const handleSaveReport = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    saveToScrapbookButton.current?.open();
  };

  let imageWidths: { [x: string]: number } = {};
  let imageHeights: { [x: string]: number } = {};

  if (small) {
    imageWidths = { xs: 80, sm: 80, md: 80, lg: 80, xl: 80 };
    imageHeights = { xs: 68, sm: 68, md: 68, lg: 68, xl: 68 };
  } else {
    imageWidths = { xs: 176, sm: 176, md: 176, lg: 176, xl: 176 };
    imageHeights = { xs: 152, sm: 152, md: 152, lg: 152, xl: 152 };
  }

  if (data.isLegacy) {
    Object.entries(imageHeights).forEach(([key]) => {
      imageHeights[key] = Math.ceil((imageWidths[key] * 337) / 600);
    });
  }

  const _onClick = (e: React.MouseEvent) => {
    onClick?.(e, data);
  };

  const showButtons = (user?.isIpBasedIdentity || user?.isAuthenticated) && showActionButtons;

  return (
    <div className={classNames(data.isLoading ? 'loading' : 'loaded', className)} {...props}>
      {data.isLoading ? (
        <div className="flex h-full flex-row gap-6 py-4">
          <div
            className={classNames(
              'flex h-full shrink-0 animate-pulse items-center justify-center bg-gray-300',
              small ? 'aspect-[80/68] w-[76px]' : 'aspect-[176/152] w-44',
            )}
          >
            <ImageIcon className="h-10 w-10 text-gray-200 dark:text-gray-600" />
          </div>
          <div className="flex w-full flex-col">
            <div className="flex justify-between">
              <div className="h-[18px] w-32 animate-pulse rounded-full bg-gray-300" />
              <div className="h-[18px] w-32 animate-pulse rounded-full bg-gray-300" />
            </div>
            <div className="mt-2 h-9 w-full animate-pulse rounded-full bg-gray-300 md:w-96" />
            {!small && <div className="mt-3 h-12 w-full animate-pulse rounded-full bg-gray-300" />}
          </div>
        </div>
      ) : (
        <Link link={data.reportUrl} className="group relative flex h-full flex-row gap-6 py-4" onClick={_onClick}>
          <div
            className={classNames(
              'relative h-fit shrink-0',
              small ? 'aspect-[76/68] w-[76px]' : 'aspect-[176/152] w-44',
            )}
          >
            {data.image && (
              <ResponsiveImage
                image={data.image}
                widths={imageWidths}
                heights={imageHeights}
                cdnProps={{ mode: 'crop' }}
              />
            )}
            {showButtons && (
              <div className="buttons pointer-events-none absolute right-0 bottom-0 hidden w-full justify-end opacity-0 transition-opacity duration-500 group-hover:opacity-100 md:flex pointer-coarse:opacity-100">
                <div className="button-list">
                  {!hideViewed && isVisitedPage && (
                    <div className="flex justify-end">
                      <ReportButton icon={CheckboxCircleIcon} text={t('general.$viewed')} textClassName="pr-7" />
                    </div>
                  )}
                  {data.readTime && parseInt(data.readTime) > 0 && (
                    <div className="flex justify-end">
                      <ReportButton icon={ClockIcon} text={t('report.$readTime', { time: data.readTime })} />
                    </div>
                  )}
                  <div className="flex justify-end">
                    <ReportButton
                      icon={EyeIcon}
                      text={t('general.$preview')}
                      onClick={handlePreview}
                      textClassName="pr-5"
                    />
                  </div>
                  {!user.isIpBasedIdentity && (
                    <div className="flex justify-end">
                      <ReportButton
                        icon={ScissorIcon}
                        text={t('general.$save')}
                        onClick={handleSaveReport}
                        textClassName="pr-10"
                      />
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
          <div className="flex w-full flex-col">
            <div className="flex justify-between text-xs leading-[18px] font-bold">
              <div>{data.directory?.name}</div>
              <div>{dayjs(data.publishedDate)?.format('DD MMMM YYYY')}</div>
            </div>
            <h4 className="pt-2 text-sm font-light md:line-clamp-2 md:text-base lg:text-lg">{data.title}</h4>
            {!small && <div className="body-1 pt-3" dangerouslySetInnerHTML={{ __html: data.description }} />}
          </div>
        </Link>
      )}

      <SaveToScrapbookButton
        ref={saveToScrapbookButton}
        scrapbookContent={{
          pageId: data.reportId,
          pageTitle: data.title,
          directory: data.directory?.name,
          readTime: data.readTime,
          publishedDate: data.publishedDate,
          pageUrl: data.reportUrl,
          thumbnailImage: data.image,
        }}
        scrapbookContentType={ScrapbookContentType.Article}
        scrapbookPath={`rprt.${data.reportId}.link.json`}
        successMessage="Report saved to scrapbook successfully"
        tooltipProps={{ inactive: true, content: ' ' }}
        className="hidden"
      >
        <ReportButton icon={ScissorIcon} text={t('general.$save')} textClassName="pr-10" />
      </SaveToScrapbookButton>
    </div>
  );
};

export default ReportListCardStylus;
